import styled from 'styled-components';
import {
  Flex,
  FlexColumn,
  ChartTooltip,
} from '@elementary/common-ui/components';
import themeBase from '@elementary/common-ui/style/theme';
import {
  formatShortDate,
  localizeDate,
} from '@elementary/common-ui/utils/datetime';
import { Typography } from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  ReferenceLine,
  XAxis,
  YAxis,
  Tooltip,
  Dot,
  CartesianGrid,
} from 'recharts';
import { space } from '@elementary/common-ui/style/utils';
import { ScoreOverTimeChartPoints } from '../../types';

const YBar = styled(Flex)`
  width: 2px;
`;

type Props = {
  height: number;
  scores: ScoreOverTimeChartPoints[];
  lowThreshold: number;
  mediumThreshold: number;
  hideXAxis?: boolean;
  small?: boolean;
};

const percentsToPixels = (value: number, height: number) =>
  Math.round((height * value) / 100);

const filterDuplicateDates = (scores: ScoreOverTimeChartPoints[]) => {
  const datesMap: { [key: string]: number } = {};

  scores.forEach((record) => {
    const date = formatShortDate(localizeDate(record.date));
    datesMap[date] = record.score;
  });

  const filteredScoreDates = Object.keys(datesMap).map((date) => ({
    date,
    score: datesMap[date],
  }));

  return filteredScoreDates;
};

const ScoreOverTimeChart = ({
  height,
  scores,
  lowThreshold,
  mediumThreshold,
  hideXAxis = true,
  small,
}: Props) => {
  const graphHeight = hideXAxis ? height : height - 12;
  const lowLength = percentsToPixels(lowThreshold, graphHeight);
  const mediumLength = percentsToPixels(
    mediumThreshold - lowThreshold,
    graphHeight,
  );
  const highLength = percentsToPixels(100 - mediumThreshold, graphHeight);
  const filteredScoreDates = filterDuplicateDates(scores);

  return (
    <Flex sx={{ position: 'relative' }}>
      <FlexColumn sx={{ position: 'absolute', left: '22px' }}>
        <YBar
          bgcolor={themeBase.palette.status.success.main}
          height={highLength}
        />
        <YBar
          bgcolor={themeBase.palette.status.warning.strong}
          height={mediumLength}
        />
        <YBar
          bgcolor={themeBase.palette.status.error.main}
          height={lowLength}
        />
      </FlexColumn>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={filteredScoreDates}>
          <CartesianGrid
            stroke={themeBase.palette.chart.grid}
            fill={themeBase.palette.icon.inverse}
          />

          {hideXAxis ? (
            <XAxis dataKey="date" hide />
          ) : (
            <XAxis
              dataKey="date"
              tickLine={false}
              tickMargin={1}
              tick={{
                fontSize: themeBase.tokens.fontSize.xxs,
                fill: themeBase.palette.icon.secondary,
              }}
              height={12}
              stroke={themeBase.palette.chart.marker}
              interval={filteredScoreDates?.length > 8 && small ? 1 : 0}
              padding={{ right: 8 }}
            />
          )}
          <YAxis
            domain={[0, 100]}
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: themeBase.tokens.fontSize.xxs }}
            tickMargin={4}
            width={22}
            ticks={[lowThreshold, mediumThreshold, 100]}
          />

          <Line
            type="linear"
            dataKey="score"
            stroke={themeBase.palette.icon.secondary}
            dot={<Dot />}
          />

          <ReferenceLine
            y={lowThreshold}
            stroke={themeBase.palette.chart.axis}
            strokeDasharray="5"
          />
          <ReferenceLine
            y={mediumThreshold}
            stroke={themeBase.palette.chart.axis}
            strokeDasharray="5"
          />
          <Tooltip
            content={({ payload }) => {
              const data =
                payload && payload.length ? payload[0].payload : null;
              const color =
                data?.score < lowThreshold
                  ? themeBase.palette.status.error.main
                  : data?.score > mediumThreshold
                  ? themeBase.palette.status.success.main
                  : themeBase.palette.status.warning.strong;
              return (
                <ChartTooltip>
                  <FlexColumn sx={{ gap: space('sm') }}>
                    <Typography>{data?.date}</Typography>
                    <Typography sx={{ color }}>
                      Score: {data?.score}%
                    </Typography>
                  </FlexColumn>
                </ChartTooltip>
              );
            }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Flex>
  );
};

export default ScoreOverTimeChart;
